import { Module } from 'vuex'

import {clampScale, IDENTITY, isOpened, Viewport} from '@/helpers/graph'
import {NodeTraversal} from '@/helpers/graph/nodeTraversal'
import {firstFreeIndex, fromEntries, intKeyedEntries, intKeys} from '@/helpers/javascript'

import {logger} from '@/logger'
import api from '@/api'
import {SchemaState} from '@/store/modules/schema'
import {Definition} from '@/api/schema'

interface Entity {
	uid: string
	id: number
	definitionName: string
	value: string
}

interface Node {
	uid: string
	id: number
	x?: number
	y?: number
	vx?: number
	vy?: number
	fx?: number | null
	fy?: number | null
	pinned?: boolean
}

const state = () => ({
	definitions: [] as string[],
	_entities: {} as Record<string, Entity>,
	_nodes: {} as Record<number, Node>,
	_links: {} as Record<number, Record<number, any[]>>,
	opened: {} as Record<string, boolean>,

	rows: [],

	viewport: { ...IDENTITY } as Viewport,
	frameSize: {w: 500, h: 500},

	_filterId: undefined as string | undefined,
	_filterValue: undefined as string | undefined,
	_selectedNodeId: undefined as number | undefined,
})

type GraphState = ReturnType<typeof state>

interface RootState {
	schema: SchemaState
}

const store: Module<GraphState, RootState> = {
	namespaced: true,
	state,
	getters: {
		usedDefinitions (state, getters, rootState): Record<string, Definition> {
			return fromEntries(state.definitions.map(d => [d, rootState.schema.entities[d]])) || {}
		},
		allDefinitions (state, getters, rootState): Record<string, Definition> {
			return fromEntries(Object.entries(rootState.schema.entities).filter((e) => e[1].type === 'String'))
		},
		isEmpty (state): boolean {
			return !Object.values(state._nodes).length
		},
		entitiesById (state): Record<number, Entity> {
			return fromEntries(Object.values(state._entities).map(e => [ e.id, e ]))
		},
		filterId (state): string | null {
			return state._filterId ?? null
		},
		filterValue (state): string | null {
			return state._filterValue ?? null
		},
		selectedNodeId (state): number | null {
			return state._selectedNodeId !== undefined ? state._selectedNodeId : null
		}
	},
	actions: {
		async fetchDefinitions ({ commit }) {
			const response = await api.graph.definitions()
			commit('SET_DEFINITIONS', response.data)
		},
		async addDefinitions ({ commit }, ids: string[]) {
			const response = await api.graph.addDefinitions(ids)
			commit('ADD_DEFINITIONS', response.data)
		},
		async removeDefinitions ({ commit, state }, ids: string[]) {
			const response = await api.graph.removeDefinitions(ids)
			const data = response.data
			const deleted = Object.values(state._entities).filter(d => data.includes(d.definitionName))
			deleted.forEach(d => commit('REMOVE_ENTITY', d))
			commit('REMOVE_DEFINITIONS', data)
		},
		async init ({ commit, state }) {
			const definitionName = state._filterId
			const value = state._filterValue
			if (definitionName && value) {
				const response = await api.graph.init({definitionName, value})
				const data = response.data
				if (data) {
					commit('CLEAR_GRAPH')
					const { id } = data
					if (!(id in state._entities)) {
						commit('lists/SET_ENTITY', { id, definitionName, value }, { root: true })
						commit('ADD_ENTITY', {
							id: firstFreeIndex(Object.keys(state._nodes), 1),
							uid: id,
							definitionName,
							value
						})
					}
				} else {
					alert('Ничего не найдено')
				}
			}
		},
		async openHub ({ commit, state, getters }, hub) {
			const host1Id = hub.hostId;
			const sourceId = state._nodes[host1Id].uid
			const definitionName = hub.definitionName
			const targetName = getters.usedDefinitions[definitionName] && definitionName

			const response = await api.graph.neighbors({ sourceId, targetName })
			for (const { id, value, txIds } of response.data) {
				const host2 = state._entities[id] && { ...state._entities[id], txIds }
					|| { id: firstFreeIndex(Object.keys(state._nodes), 1), uid: id, definitionName, value, txIds }
				if (!(id in state._entities)) {
					commit('lists/SET_ENTITY', { id, definitionName, value }, { root: true })
				}
				commit('ADD_LINK', { host1Id, host2 })
			}
			commit('SET_HUB_STATUS', { hub, status: true })
		},
		closeHub ({ commit, state, getters }, hub) {
			const { definitionName } = getters.entitiesById[hub.hostId]
			const nodeTraversal = new NodeTraversal(state._links, state._nodes)
			const neighbors = nodeTraversal.neighbors(hub.hostId).map(id => getters.entitiesById[id] as Entity)
				.filter(e => e.definitionName === hub.definitionName).map(e => e.id)
			for (const neighbor of neighbors) {
				if (!isOpened({ hostId: neighbor, definitionName }, state.opened)) {
					commit('REMOVE_ENTITY', getters.entitiesById[neighbor])
					nodeTraversal.delete(neighbor)
				}
			}
			nodeTraversal.splitByNodeId(hub.hostId).outer.map(id => getters.entitiesById[id])
				.forEach(entity => commit('REMOVE_ENTITY', entity))
			commit('SET_HUB_STATUS', { hub, status: false })
		},
		selectNode ({ commit }, entity) {
			commit('SET_SELECTED_NODE_ID', entity.id)
		},
		deselectNode ({ commit }) {
			commit('SET_SELECTED_NODE_ID', null)
		},
		async txData ({ commit }, { txIds, properties }) {
			const response = await api.graph.txData({ txIds, properties })
			commit('SET_TX_DATA', response.data)
		},
	},
	mutations: {
		SET_NODES_COORDINATES (state, nodes: Node[]) {
			state._nodes = Object.assign(state._nodes, fromEntries(nodes.filter(n => n.id in state._nodes).map(node => {
				const { x, y, vx, vy } = node
				const target = { ...state._nodes[node.id] }
				if (x || x === 0) {
					target.x = x
				}
				if (y || y === 0) {
					target.y = y
				}
				if (vx || vx === 0) {
					target.vx = vx
				}
				if (vy || vy === 0) {
					target.vy = vy
				}
				// fx/fy and pinned are assigned unconditionally so that a null can release a pin
				if ('fx' in node) {
					target.fx = node.fx
				}
				if ('fy' in node) {
					target.fy = node.fy
				}
				if ('pinned' in node) {
					target.pinned = node.pinned
				}
				return [ node.id, target ];
			})))
		},
		SET_VIEWPORT (state, { x, y, k }: Viewport) {
			state.viewport = { x, y, k: clampScale(k) }
		},
		RESET_VIEWPORT (state) {
			state.viewport = { ...IDENTITY }
		},
		SET_FRAME_SIZE (state, { w, h }) {
			state.frameSize = { w, h }
		},
		SET_DEFINITIONS (state, definitions: string[]) {
			state.definitions = definitions
		},
		ADD_DEFINITIONS (state, definitions: string[]) {
			state.definitions = state.definitions.concat(definitions)
		},
		REMOVE_DEFINITIONS (state, definitions) {
			state.definitions = state.definitions.filter((d) => !definitions.includes(d))
		},
		ADD_ENTITY (state, { id, uid, definitionName, value }: { id: number, uid: string, definitionName: string, value: string }) {
			logger.log('ADD_ENTITY', { id, uid, definitionName, value })
			state._entities[uid] = { id, uid, definitionName, value }
			state._nodes[id] = { id, uid }
		},
		REMOVE_ENTITY (state, { id, uid }) {
			logger.log('REMOVE_ENTITY', { id, uid })
			state._links = fromEntries(intKeyedEntries(state._links).filter(e => e[0] !== id).map(e => [e[0], fromEntries(intKeyedEntries(e[1]).filter(l => l[0] !== id))]))
			state.opened = fromEntries(Object.entries(state.opened).filter(o => !(new RegExp('^' + id + ',\\d+$')).test(o[0])))
			delete state._entities[uid]
			delete state._nodes[id]
		},
		ADD_LINK (state, { host1Id, host2 }) {
			logger.log('ADD_LINK', { host1Id, host2 })
			const { id: host2Id, uid: host2Uid, txIds } = host2
			if (!(host2Uid in state._entities)) {
				state._entities[host2Uid] = host2
				state._nodes[host2Id] = { uid: host2Uid, id: host2Id }
			}
			const lesser = Math.min(host1Id, host2Id)
			const greater = Math.max(host1Id, host2Id)
			state._links[lesser] = Object.assign(state._links[lesser] || {}, { [greater]: txIds })
		},
		REMOVE_LINK (state, { host1Id, host2Id }) {
			logger.log('REMOVE_LINK', { host1Id, host2Id })
			const lesser = Math.min(host1Id, host2Id)
			const greater = Math.max(host1Id, host2Id)
			const links = state._links[lesser] || {}
			delete links[greater]
			state._links[lesser] = links
		},
		SET_HUB_STATUS (state, { hub, status }) {
			logger.log('SET_HUB_STATUS', { hub, status })
			state.opened = { ...state.opened, [hub.id]: status }
		},
		CLEAR_GRAPH (state) {
			state.opened = {}
			state._selectedNodeId = undefined
			state._links = {}
			state._nodes = {}
			state._entities = {}
		},
		SET_FILTER_ID (state, definitionName: string | null = null) {
			state._filterId = definitionName ?? undefined
		},
		SET_FILTER_VALUE (state, value: string | null = null) {
			state._filterValue = value ?? undefined
		},
		SET_SELECTED_NODE_ID (state, nodeId: number | null = null) {
			state._selectedNodeId = nodeId !== null ? nodeId : undefined
		},
		SET_TX_DATA (state, data) {
			state.rows = data
		},
	}
}

export default store
